/* ==========================================================================
   SMT Drip Warehouse  —  smt-dw-cart.js  (v1.0.0)
   The bag. Vanilla JS, no framework. Defines the <cart-drawer> element with
   the API the rest of the theme already talks to (open, close,
   renderContents, getSectionsToRender, setActiveElement) and drives every
   bag surface on the page ([data-dw-cart] roots: the drawer, the /cart page):
     - quantity steppers + remove through /cart/change.js
     - re-render from the Section Rendering API (server Liquid owns all state)
     - header count, live-region announcements, focus trap, scroll lock
     - pubsub bridge so legacy Dawn subscribers and Standard Actions stay in sync
   Loaded with `defer` after smt-dw.js (cards inside the bag reuse its quick add).
   ========================================================================== */
(function () {
  'use strict';
  if (window.SMTDWCart) return;
  var API = (window.SMTDWCart = { version: '1.0.0' });

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var routes = window.routes || {};
  var CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';

  function DW() { return window.SMTDW || null; }
  function money(cents, fmt) { var d = DW(); return d ? d.money(cents, fmt) : '$' + (cents / 100).toFixed(2); }
  function drawer() { return $('cart-drawer[data-dw-cart-drawer]'); }

  /* ---------- roots + rendering ---------------------------------------------- */
  function roots() { return $$('[data-dw-cart][data-dw-section]'); }
  function ids() {
    var list = [];
    roots().forEach(function (r) { var id = r.dataset.dwSection; if (id && list.indexOf(id) < 0) list.push(id); });
    return list;
  }
  function mountOf(root) { return $('[data-dw-cart-mount]', root) || root; }
  function afterRender(root) {
    var inner = $('[data-dw-cart-inner]', root);
    if (inner) syncCount(Number(inner.dataset.dwCount || 0));
    var d = DW();
    if (d && typeof d.boot === 'function') { try { d.boot(root); } catch (e) {} }
  }
  /* Swap in fresh markup for every root whose section is in the payload. */
  function apply(sections) {
    if (!sections) return false;
    var did = false;
    roots().forEach(function (root) {
      var html = sections[root.dataset.dwSection];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var freshMount = doc.querySelector('[data-dw-cart-mount]');
      var freshRoot = doc.querySelector('[data-dw-cart]');
      if (!freshMount) return;
      mountOf(root).innerHTML = freshMount.innerHTML;
      root.classList.toggle('is-empty', !!(freshRoot && freshRoot.classList.contains('is-empty')));
      afterRender(root);
      did = true;
    });
    return did;
  }
  function refresh() {
    var list = ids();
    if (!list.length) return Promise.resolve(false);
    var url;
    try { url = new URL(window.location.href); url.hash = ''; url.searchParams.set('sections', list.join(',')); url = url.toString(); }
    catch (e) { url = window.location.pathname + '?sections=' + encodeURIComponent(list.join(',')); }
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) { return apply(sections); })
      .catch(function () { return false; });
  }
  function syncCount(n) {
    $$('.smt-header__cart').forEach(function (link) {
      var badge = link.querySelector(':scope > span');
      if (n > 0) {
        if (!badge) { badge = document.createElement('span'); link.appendChild(badge); }
        badge.textContent = n; badge.hidden = false;
      } else if (badge) { badge.hidden = true; }
      link.setAttribute('aria-label', 'Bag, ' + n + (n === 1 ? ' item' : ' items'));
    });
    $$('[data-dw-cart-count]').forEach(function (el) { el.textContent = n; el.hidden = n === 0; });
    var bubble = $('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    if (bubble) bubble.textContent = n;
  }
  function announce(root, text) {
    var live = $('[data-dw-cart-live]', root);
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 40);
  }
  function publishUpdate(cart) {
    document.dispatchEvent(new CustomEvent('smt:cart:updated', { detail: { source: 'dw-cart', cart: cart } }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'dw-cart', cartData: cart }); } catch (e) {}
    }
  }

  /* ---------- quantity changes --------------------------------------------- */
  var timers = new WeakMap();
  function lineOf(el) { return el.closest('[data-dw-line]'); }
  function rootOf(el) { return el.closest('[data-dw-cart]'); }
  function schedule(line) {
    clearTimeout(timers.get(line));
    timers.set(line, setTimeout(function () { commit(line); }, 260));
  }
  function commit(line) {
    var root = rootOf(line);
    var input = $('[data-dw-qty-input]', line);
    if (!root || !input) return;
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (qty === Number(line.dataset.qty)) return;
    change(root, line, line.dataset.key, qty);
  }
  function change(root, line, key, qty) {
    if (!key || line.classList.contains('is-busy')) return Promise.resolve();
    line.classList.add('is-busy');
    var err = $('[data-dw-line-error]', line);
    if (err) err.hidden = true;
    var body = { id: key, quantity: qty, sections: ids().join(','), sections_url: window.location.pathname };
    return fetch(CHANGE_URL, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || 'Could not update your bag. Try again.');
        return j;
      });
    }).then(function (cart) {
      if (cart.errors) throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Could not update your bag. Try again.');
      var applied = apply(cart.sections);
      if (!applied) return refresh().then(function () { return cart; });
      return cart;
    }).then(function (cart) {
      if (!cart) return;
      var inner = $('[data-dw-cart-inner]', root);
      var fmt = inner ? inner.dataset.dwMoney : undefined;
      announce(root, 'Bag updated. ' + cart.item_count + (cart.item_count === 1 ? ' item. ' : ' items. ') + 'Subtotal ' + money(cart.total_price, fmt) + '.');
      publishUpdate(cart);
    }).catch(function (e) {
      line.classList.remove('is-busy');
      var input = $('[data-dw-qty-input]', line);
      if (input) input.value = line.dataset.qty;
      if (err) { err.textContent = e && e.message ? e.message : 'Could not update your bag. Try again.'; err.hidden = false; }
      var d = DW(); if (d) d.toast(err ? err.textContent : 'Could not update your bag.');
    });
  }

  /* ---------- the drawer element ------------------------------------------- */
  function focusables(root) {
    return $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', root)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  class DWCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._trigger = null;
      this._onKey = this._onKey.bind(this);
    }
    connectedCallback() { this.sectionId = this.dataset.dwSection || 'smt-dw-cart-drawer'; }
    panel() { return $('[data-dw-cart-mount]', this); }
    _onKey(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key !== 'Tab') return;
      var panel = this.panel();
      var f = focusables(panel);
      if (!f.length) { e.preventDefault(); if (panel) panel.focus(); return; }
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this._trigger = trigger && typeof trigger.focus === 'function' ? trigger : document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('dw-cart-lock');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this._onKey);
      var self = this;
      setTimeout(function () {
        var target = $('.dw-cart__close', self) || self.panel();
        if (target && target.focus) target.focus({ preventScroll: true });
      }, 80);
      document.dispatchEvent(new CustomEvent('smt:cart:open'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('dw-cart-lock');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this._onKey);
      var t = this._trigger; this._trigger = null;
      if (t && typeof t.focus === 'function' && document.contains(t)) t.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('smt:cart:close'));
    }
    /* Dawn-compatible surface: product-form.js, quick-add, Standard Actions and
       smt-dw.js all call these. `sections` may carry any of our roots. */
    getSectionsToRender() { return [{ id: this.sectionId, selector: '#dw-cart-drawer' }]; }
    renderContents(parsedState, opts) {
      var sections = parsedState && parsedState.sections;
      var self = this;
      var done = sections && apply(sections) ? Promise.resolve(true) : refresh();
      if (!(opts && opts.open === false)) done.then(function () { self.open(); });
      return done;
    }
    setActiveElement(el) { this._trigger = el; }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', DWCartDrawer);

  /* ---------- wiring ----------------------------------------------------------- */
  function onClick(e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    var opener = t.closest('[data-dw-open-cart], .smt-header__cart, .smt-sarc-header__cart, #cart-icon-bubble');
    if (opener) {
      var d = drawer();
      if (d) { e.preventDefault(); d.open(opener); }
      return;
    }
    var closer = t.closest('[data-dw-close]');
    if (closer) { var dd = closer.closest('cart-drawer'); if (dd && dd.close) dd.close(); return; }

    var qty = t.closest('[data-dw-qty]');
    if (qty) {
      var line = lineOf(qty), input = line && $('[data-dw-qty-input]', line);
      if (!line || !input) return;
      var n = Math.max(0, (parseInt(input.value, 10) || 0) + (qty.dataset.dwQty === 'minus' ? -1 : 1));
      input.value = n;
      schedule(line);
      return;
    }
    var rm = t.closest('[data-dw-remove]');
    if (rm) {
      var l2 = lineOf(rm), r2 = rootOf(rm);
      if (l2 && r2) { clearTimeout(timers.get(l2)); change(r2, l2, l2.dataset.key, 0); }
    }
  }
  function onChange(e) {
    var input = e.target;
    if (!(input instanceof Element) || !input.matches('[data-dw-qty-input]')) return;
    var line = lineOf(input);
    if (line) { clearTimeout(timers.get(line)); commit(line); }
  }
  function onSubmit(e) {
    /* Enter inside a quantity field should update, not submit to checkout. */
    var form = e.target;
    if (!(form instanceof Element) || !form.matches('[data-dw-cart-form]')) return;
    var sub = e.submitter;
    if (sub && sub.name === 'checkout') return;
    e.preventDefault();
    $$('[data-dw-line]', form).forEach(function (line) { clearTimeout(timers.get(line)); commit(line); });
  }

  function boot() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('submit', onSubmit);
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (ev) {
          if (!ev || ev.source === 'dw-cart') return;
          if (ev.cartData && ev.cartData.sections && apply(ev.cartData.sections)) return;
          if (ev.source === 'external-refresh') { roots().forEach(afterRender); return; }
          refresh();
        });
      } catch (e) {}
    }
    /* legacy scripts announce adds without sections; catch up quietly */
    document.addEventListener('smt:cart:add', function () { refresh(); });
    roots().forEach(function (root) {
      var inner = $('[data-dw-cart-inner]', root);
      if (inner) syncCount(Number(inner.dataset.dwCount || 0));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  API.refresh = refresh;
  API.apply = apply;
  API.roots = roots;
  API.ids = ids;
  API.drawer = drawer;
})();
